import { Button, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import {
  planWithCadenceLabel,
  useCatalogTierDisplayNames,
  cardLabel,
  chargeLineCopy,
  formatPlanDate,
} from './lib/helpers'
import {
  RECEIPT_SHELL,
  ReceiptCallout,
  ReceiptCardBody,
  ReceiptHeaderRow,
  ReceiptRowGroup,
  type ReceiptRow,
} from './ReceiptParts'
import type { AppliedPlanChange } from './lib/planChange.types'

export interface AppliedReceiptProps {
  result: AppliedPlanChange
  onDone: () => void
}

export const AppliedReceipt = ({ result, onDone }: AppliedReceiptProps) => {
  const names = useCatalogTierDisplayNames()
  const { invoicedAmountCents, toTier, toCadence, nextBillingAt } = result
  const card = cardLabel(result.cardBrand, result.cardLast4)
  const wasCharged = invoicedAmountCents > 0

  const rows: ReceiptRow[] = []
  if (toTier && toCadence) {
    rows.push({
      testId: 'receipt-row-plan',
      label: 'Plan changed to',
      value: planWithCadenceLabel(names[toTier], toCadence),
    })
  }
  // At zero the callout already says no charge was made, so a $0.00 row only repeats it.
  if (invoicedAmountCents !== 0) {
    rows.push({
      testId: 'receipt-row-amount',
      label: invoicedAmountCents > 0 ? 'Amount charged' : 'Amount credited',
      value: formatCurrencyFromCents(Math.abs(invoicedAmountCents)),
    })
  }
  if (card && wasCharged) {
    rows.push({
      testId: 'receipt-row-card',
      label: 'Card charged',
      value: card,
    })
  }
  if (nextBillingAt) {
    rows.push({
      testId: 'receipt-row-next-billing',
      label: 'Next billing date',
      value: formatPlanDate(nextBillingAt),
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-[560px] flex-col gap-5">
      <Text
        variant={TypographyStyles.EYEBROW}
        className="text-neutral-500 hidden md:block"
      >
        Plan change
      </Text>

      <div className="flex flex-col gap-2">
        <Text
          variant={TypographyStyles.DISPLAY_SM}
          className="md:text-[32px]"
          role="heading"
          aria-level={1}
        >
          Your plan has changed
        </Text>
        <Text variant={TypographyStyles.BODY_MD} className="text-neutral-600">
          Here&apos;s your receipt.
        </Text>
      </div>

      <div className={RECEIPT_SHELL}>
        <ReceiptHeaderRow createdAt={result.createdAt} />
        <ReceiptCardBody>
          <ReceiptCallout
            tone="moss"
            headline={chargeLineCopy(invoicedAmountCents)}
            subline={wasCharged && card ? card : undefined}
          />
          <ReceiptRowGroup rows={rows} />
          {wasCharged && (
            <Text
              variant={TypographyStyles.BODY_SM}
              className="text-neutral-500"
            >
              You can find this receipt anytime in your billing history.
            </Text>
          )}
        </ReceiptCardBody>
      </div>

      <Button className="w-full md:w-auto h-11 md:h-10" onClick={onDone}>
        Done
      </Button>
    </div>
  )
}
